import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLangFromPath, swapLangInPath, buildLocalizedPath } from '../utils/i18n';

const LanguageContext = createContext();

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
};

/**
 * URL-driven language provider.
 * Must be mounted INSIDE <BrowserRouter> because it depends on useLocation.
 *
 * - `language` is derived from the URL ('/pt/...' -> 'pt', else 'en').
 * - `toggleLanguage()` navigates to the equivalent URL in the other language,
 *   preserving the current page slug.
 * - `localizedPath('/advisory')` returns '/advisory' (EN) or '/pt/advisory' (PT).
 */
export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const language = getLangFromPath(location.pathname);

  const toggleLanguage = useCallback(() => {
    const next = swapLangInPath(location.pathname + (location.search || ''));
    navigate(next);
  }, [location.pathname, location.search, navigate]);

  const setLanguage = useCallback(
    (lang) => {
      if (lang === language) return;
      const next = swapLangInPath(location.pathname + (location.search || ''));
      navigate(next);
    },
    [language, location.pathname, location.search, navigate]
  );

  const localizedPath = useCallback((path) => buildLocalizedPath(language, path), [language]);

  // Backwards-compat helper used in some legacy components.
  const t = useCallback((pt, en) => (language === 'pt' ? pt : en || pt), [language]);

  const value = useMemo(
    () => ({ language, setLanguage, toggleLanguage, localizedPath, t }),
    [language, setLanguage, toggleLanguage, localizedPath, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
